/**
 * DeviceGroupCard - Apple Home style group control card
 * Displays a device control group with the SAME appearance as individual device cards
 * Uses the same DeviceIcon component and layout template
 */

import React, { useMemo } from 'react';
import { GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import LayersIcon from '@mui/icons-material/Layers';
import { CSS } from '@dnd-kit/utilities';
import { DeviceControlGroup } from '../services/userPreferencesApi';
import { DeviceIcon } from './deviceIcons';

interface DeviceStatus {
  deviceId: string;
  isOn?: boolean;
  online?: boolean;
  brightness?: number;
  colorTemp?: number;
  temperature?: number;
  setTemperature?: number;
  openPercent?: number;
  fanSpeed?: string;
  mode?: string;
  [key: string]: any;
}

interface DeviceGroupCardProps {
  group: DeviceControlGroup;
  deviceStatuses: Record<string, DeviceStatus>;
  onClick: (group: DeviceControlGroup) => void;
  onToggle?: (group: DeviceControlGroup, newState: boolean) => void;
}

// Map group deviceType to DeviceIcon deviceType
const mapDeviceType = (type: string): string => {
  const typeMap: Record<string, string> = {
    'ac': 'hvac',
  };
  return typeMap[type] || type;
};

const DeviceGroupCard: React.FC<DeviceGroupCardProps> = ({
  group,
  deviceStatuses,
  onClick,
  onToggle
}) => {
  // Aggregate status from all devices in the group
  const aggregatedStatus = useMemo(() => {
    const statuses = group.devices
      .map(sn => deviceStatuses[sn])
      .filter(Boolean);
    
    if (statuses.length === 0) {
      return {
        anyOn: false,
        allOnline: false,
        onlineCount: 0,
        offlineCount: group.devices.length,
        onCount: 0,
        totalCount: group.devices.length,
        avgBrightness: 0,
        avgSetTemperature: 0,
        avgOpenPercent: 0
      };
    }
    
    const onlineStatuses = statuses.filter(s => s.online !== false);
    const onStatuses = statuses.filter(s => s.isOn === true);
    const brightnessValues = statuses.map(s => s.brightness).filter(v => typeof v === 'number') as number[];
    const setTempValues = statuses.map(s => s.setTemperature).filter(v => typeof v === 'number') as number[];
    const openPercentValues = statuses.map(s => s.openPercent).filter(v => typeof v === 'number') as number[];
    
    return {
      anyOn: onStatuses.length > 0,
      allOnline: onlineStatuses.length === statuses.length,
      onlineCount: onlineStatuses.length,
      offlineCount: statuses.length - onlineStatuses.length,
      onCount: onStatuses.length,
      totalCount: statuses.length,
      avgBrightness: brightnessValues.length > 0 
        ? Math.round(brightnessValues.reduce((a, b) => a + b, 0) / brightnessValues.length) 
        : 0,
      avgSetTemperature: setTempValues.length > 0 
        ? Math.round(setTempValues.reduce((a, b) => a + b, 0) / setTempValues.length) 
        : 0,
      avgOpenPercent: openPercentValues.length > 0 
        ? Math.round(openPercentValues.reduce((a, b) => a + b, 0) / openPercentValues.length) 
        : 0
    };
  }, [group.devices, deviceStatuses]);

  const isDeviceOn = aggregatedStatus.anyOn;
  const isOffline = aggregatedStatus.offlineCount === group.devices.length;
  const isOnline = !isOffline;

  // Check if device type has OpenClose control
  const hasOpenCloseControl = group.deviceType === 'curtain' || group.deviceType === 'blind';
  const hasQuickControl = onToggle && isOnline;

  // Card styling - matching individual device card styles
  const getCardStyle = () => {
    if (isOffline) {
      return 'border-transparent';
    }
    if (isDeviceOn) {
      return 'border-transparent shadow-lg';
    }
    return 'border-transparent';
  };

  // Info area background style
  const getInfoAreaStyle = () => {
    if (isOffline) {
      return 'bg-gradient-to-br from-gray-800 to-gray-900';
    }
    if (isDeviceOn) {
      return 'bg-slate-800/50';
    }
    return 'bg-gradient-to-br from-gray-800 to-gray-900';
  };

  // Icon area background style
  const getIconAreaStyle = () => {
    if (isOffline) {
      return 'bg-gradient-to-br from-red-900 to-red-950';
    }
    if (isDeviceOn) {
      return 'bg-gradient-to-br from-gray-100 via-gray-200 to-gray-100 shadow-[inset_0_1px_3px_rgba(0,0,0,0.08)]';
    }
    return 'bg-gradient-to-br from-gray-500 to-gray-600';
  };

  const getSubTextStyle = () => {
    if (isDeviceOn && isOnline) {
      return 'text-slate-400';
    }
    return 'text-slate-400';
  };

  const handleControlAreaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle?.(group, !aggregatedStatus.anyOn);
  };

  // Map deviceType for DeviceIcon component
  const iconDeviceType = mapDeviceType(group.deviceType);

  // Get state values for display (matching individual card logic)
  type StateValue = { num: string; unit?: string; color: string } | null;
  const getGroupStates = (): { state1: StateValue; state2: StateValue } => {
    const offlineColor = 'text-slate-400';
    
    // Light: brightness %
    if (group.deviceType === 'light') {
      if (!isDeviceOn) {
        return { state1: null, state2: null };
      }
      return {
        state1: aggregatedStatus.avgBrightness > 0 
          ? { num: `${aggregatedStatus.avgBrightness}`, unit: '%', color: isOnline ? 'text-orange-500' : offlineColor }
          : null,
        state2: null
      };
    }
    
    // HVAC: setTemperature + TARGET
    if (group.deviceType === 'hvac' || group.deviceType === 'ac') {
      if (!isDeviceOn) {
        return { state1: null, state2: null };
      }
      return {
        state1: aggregatedStatus.avgSetTemperature > 0 
          ? { num: `${aggregatedStatus.avgSetTemperature}`, unit: '°C', color: isOnline ? 'text-cyan-500' : offlineColor }
          : null,
        state2: aggregatedStatus.avgSetTemperature > 0
          ? { num: '', unit: 'TARGET', color: isOnline ? 'text-slate-400' : offlineColor }
          : null
      };
    }
    
    // Curtain/Blind: openPercent
    if (group.deviceType === 'curtain' || group.deviceType === 'blind') {
      if (!isDeviceOn) {
        return { state1: null, state2: null };
      }
      return {
        state1: aggregatedStatus.avgOpenPercent >= 0 
          ? { num: `${aggregatedStatus.avgOpenPercent}`, unit: '%', color: isOnline ? 'text-blue-500' : offlineColor }
          : null,
        state2: null
      };
    }
    
    // Fan: just show ON state (no speed aggregation for now)
    if (group.deviceType === 'fan') {
      return { state1: null, state2: null };
    }
    
    // Switch/Outlet: no state display (icon shows ON/OFF)
    return { state1: null, state2: null };
  };

  const { state1, state2 } = getGroupStates();

  // Render state with same styling as individual cards
  const renderState = (state: StateValue, isPrimary: boolean) => {
    if (!state) return null;
    const isDegree = state.unit === '°' || state.unit === '°C';
    const isNumeric = /^[\d.,\-]+$/.test(state.num);
    const numSize = isPrimary 
      ? (isNumeric ? 'text-5xl' : 'text-4xl')
      : (isNumeric ? 'text-4xl' : 'text-3xl');
    const unitSize = isPrimary ? 'text-xl' : 'text-lg';
    return (
      <span className={`font-bold ${state.color}`}>
        <span className={numSize}>{state.num}</span>
        {state.unit && (
          <span className={`${unitSize} ${isDegree ? 'align-top' : ''}`}>{state.unit}</span>
        )}
      </span>
    );
  };

  // TAP hint text
  const getTapHintText = () => {
    if (hasOpenCloseControl) {
      return isDeviceOn ? 'TAP CLOSE' : 'TAP OPEN';
    }
    return isDeviceOn ? 'TAP OFF' : 'TAP ON';
  };

  return (
    <div
      onClick={() => onClick(group)}
      className={`group flex flex-row rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200 ease-out will-change-transform h-[162px] ${getCardStyle()}`}
    >
      {/* Left: Icon Area - 20% drag, 60% icon, 20% TAP hint (matching individual card) */}
      <div className={`w-[100px] flex-shrink-0 flex flex-col py-3 transition-all duration-150 ${getIconAreaStyle()}`}>
        {/* Drag Handle (20%) */}
        <div className="flex-[1] flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-white/5 transition-colors">
          <GripVertical className="w-4 h-4 text-slate-400" />
        </div>
        
        {/* Icon area (60%) - clickable for ON/OFF control */}
        <div 
          onClick={hasQuickControl ? handleControlAreaClick : undefined}
          className={`flex-[3] flex items-center justify-center relative ${hasQuickControl ? 'cursor-pointer' : 'cursor-default'}`}
        >
          {/* OFF state: show OFF icon normally, ON icon on hover */}
          {hasQuickControl && !isDeviceOn ? (
            <>
              {/* OFF icon - normal, hidden on hover */}
              <div className="group-hover:opacity-0 transition-opacity duration-150">
                <DeviceIcon 
                  deviceType={iconDeviceType as any} 
                  isOn={false} 
                  size={40} 
                  animated={true}
                />
              </div>
              {/* ON icon - hidden, shown on hover */}
              <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                <DeviceIcon 
                  deviceType={iconDeviceType as any} 
                  isOn={true} 
                  size={40} 
                  animated={true}
                />
              </div>
            </>
          ) : (
            <DeviceIcon 
              deviceType={iconDeviceType as any} 
              isOn={isDeviceOn} 
              size={40} 
              animated={true}
            />
          )}
        </div>
        
        {/* TAP hint area (20%) */}
        <div className="flex-[1] flex items-center justify-center">
          {hasQuickControl && (
            <span className={`text-[10px] font-bold transition-colors duration-150 ${
              isDeviceOn 
                ? 'text-slate-800' 
                : 'text-slate-400 group-hover:text-white group-hover:drop-shadow-[0_0_4px_rgba(255,255,255,0.5)]'
            }`}>
              {getTapHintText()}
            </span>
          )}
        </div>
      </div>

      {/* Right: Info Area - 3 sections: Name (20%), State (60%), Location+Battery (20%) */}
      <div className={`flex-1 p-3 flex flex-col min-w-0 ${getInfoAreaStyle()}`}>
        {/* Section 1: Group Name (20%) - same as device name */}
        <div className="flex-[1] flex items-center">
          <h4 className="font-bold truncate text-sm text-white">
            {group.name}
          </h4>
        </div>
        
        {/* Section 2: State Area (60%) - Two columns layout */}
        <div className="flex-[3] flex items-center gap-3">
          {renderState(state1, true)}
          {renderState(state2, false)}
        </div>
        
        {/* Section 3: Group indicator (replacing location) (20%) */}
        <div className="flex-[1] flex items-center justify-between">
          <div className="flex items-center gap-1">
            <LayersIcon className="w-3.5 h-3.5 text-blue-400" />
            <p className={`text-xs font-bold ${getSubTextStyle()}`}>
              群組({group.devices.length})
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sortable version for drag-and-drop in Control Page
export const SortableDeviceGroupCard: React.FC<DeviceGroupCardProps & { id: string }> = ({
  id,
  group,
  deviceStatuses,
  onClick,
  onToggle
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onClick(group)}
      className={`group flex flex-row rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200 ease-out will-change-transform h-[162px] ${isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''}`}
    >
      {/* Reuse the card content but with drag handlers on the grip */}
      <DeviceGroupCardContent
        group={group}
        deviceStatuses={deviceStatuses}
        onToggle={onToggle}
        dragListeners={listeners}
        dragAttributes={attributes}
      />
    </div>
  );
};

// Internal content component to share between regular and sortable versions
const DeviceGroupCardContent: React.FC<{
  group: DeviceControlGroup;
  deviceStatuses: Record<string, DeviceStatus>;
  onToggle?: (group: DeviceControlGroup, newState: boolean) => void;
  dragListeners?: any;
  dragAttributes?: any;
}> = ({ group, deviceStatuses, onToggle, dragListeners, dragAttributes }) => {
  // Aggregate status from all devices in the group
  const aggregatedStatus = useMemo(() => {
    const statuses = group.devices
      .map(sn => deviceStatuses[sn])
      .filter(Boolean);
    
    if (statuses.length === 0) {
      return {
        anyOn: false,
        allOnline: false,
        offlineCount: group.devices.length,
        onCount: 0,
        totalCount: group.devices.length,
        avgBrightness: 0,
        avgSetTemperature: 0,
        avgOpenPercent: 0
      };
    }
    
    const onlineStatuses = statuses.filter(s => s.online !== false);
    const onStatuses = statuses.filter(s => s.isOn === true);
    const brightnessValues = statuses.map(s => s.brightness).filter(v => typeof v === 'number') as number[];
    const setTempValues = statuses.map(s => s.setTemperature).filter(v => typeof v === 'number') as number[];
    const openPercentValues = statuses.map(s => s.openPercent).filter(v => typeof v === 'number') as number[];
    
    return {
      anyOn: onStatuses.length > 0,
      allOnline: onlineStatuses.length === statuses.length,
      offlineCount: statuses.length - onlineStatuses.length,
      onCount: onStatuses.length,
      totalCount: statuses.length,
      avgBrightness: brightnessValues.length > 0 
        ? Math.round(brightnessValues.reduce((a, b) => a + b, 0) / brightnessValues.length) 
        : 0,
      avgSetTemperature: setTempValues.length > 0 
        ? Math.round(setTempValues.reduce((a, b) => a + b, 0) / setTempValues.length) 
        : 0,
      avgOpenPercent: openPercentValues.length > 0 
        ? Math.round(openPercentValues.reduce((a, b) => a + b, 0) / openPercentValues.length) 
        : 0
    };
  }, [group.devices, deviceStatuses]);

  const isDeviceOn = aggregatedStatus.anyOn;
  const isOffline = aggregatedStatus.offlineCount === group.devices.length;
  const isOnline = !isOffline;
  const hasOpenCloseControl = group.deviceType === 'curtain' || group.deviceType === 'blind';
  const hasQuickControl = onToggle && isOnline;

  const getIconAreaStyle = () => {
    if (isOffline) return 'bg-gradient-to-br from-red-900 to-red-950';
    if (isDeviceOn) return 'bg-gradient-to-br from-gray-100 via-gray-200 to-gray-100 shadow-[inset_0_1px_3px_rgba(0,0,0,0.08)]';
    return 'bg-gradient-to-br from-gray-500 to-gray-600';
  };

  const getInfoAreaStyle = () => {
    if (isOffline) return 'bg-gradient-to-br from-gray-800 to-gray-900';
    if (isDeviceOn) return 'bg-slate-800/50';
    return 'bg-gradient-to-br from-gray-800 to-gray-900';
  };

  const handleControlAreaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle?.(group, !aggregatedStatus.anyOn);
  };

  const iconDeviceType = group.deviceType === 'ac' ? 'hvac' : group.deviceType;

  // Get state values
  type StateValue = { num: string; unit?: string; color: string } | null;
  const getGroupStates = (): { state1: StateValue; state2: StateValue } => {
    const offlineColor = 'text-slate-400';
    
    if (group.deviceType === 'light') {
      if (!isDeviceOn) return { state1: null, state2: null };
      return {
        state1: aggregatedStatus.avgBrightness > 0 
          ? { num: `${aggregatedStatus.avgBrightness}`, unit: '%', color: isOnline ? 'text-orange-500' : offlineColor }
          : null,
        state2: null
      };
    }
    
    if (group.deviceType === 'hvac' || group.deviceType === 'ac') {
      if (!isDeviceOn) return { state1: null, state2: null };
      return {
        state1: aggregatedStatus.avgSetTemperature > 0 
          ? { num: `${aggregatedStatus.avgSetTemperature}`, unit: '°C', color: isOnline ? 'text-cyan-500' : offlineColor }
          : null,
        state2: aggregatedStatus.avgSetTemperature > 0
          ? { num: '', unit: 'TARGET', color: 'text-slate-400' }
          : null
      };
    }
    
    if (group.deviceType === 'curtain' || group.deviceType === 'blind') {
      if (!isDeviceOn) return { state1: null, state2: null };
      return {
        state1: { num: `${aggregatedStatus.avgOpenPercent}`, unit: '%', color: isOnline ? 'text-blue-500' : offlineColor },
        state2: null
      };
    }
    
    return { state1: null, state2: null };
  };

  const { state1, state2 } = getGroupStates();

  const renderState = (state: StateValue, isPrimary: boolean) => {
    if (!state) return null;
    const isDegree = state.unit === '°' || state.unit === '°C';
    const isNumeric = /^[\d.,\-]+$/.test(state.num);
    const numSize = isPrimary ? (isNumeric ? 'text-5xl' : 'text-4xl') : (isNumeric ? 'text-4xl' : 'text-3xl');
    const unitSize = isPrimary ? 'text-xl' : 'text-lg';
    return (
      <span className={`font-bold ${state.color}`}>
        <span className={numSize}>{state.num}</span>
        {state.unit && <span className={`${unitSize} ${isDegree ? 'align-top' : ''}`}>{state.unit}</span>}
      </span>
    );
  };

  const getTapHintText = () => {
    if (hasOpenCloseControl) return isDeviceOn ? 'TAP CLOSE' : 'TAP OPEN';
    return isDeviceOn ? 'TAP OFF' : 'TAP ON';
  };

  return (
    <>
      {/* Left: Icon Area */}
      <div className={`w-[100px] flex-shrink-0 flex flex-col py-3 transition-all duration-150 ${getIconAreaStyle()}`}>
        {/* Drag Handle */}
        <div 
          {...dragListeners}
          {...dragAttributes}
          className="flex-[1] flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-white/5 transition-colors"
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </div>
        
        {/* Icon area */}
        <div 
          onClick={hasQuickControl ? handleControlAreaClick : undefined}
          className={`flex-[3] flex items-center justify-center relative ${hasQuickControl ? 'cursor-pointer' : 'cursor-default'}`}
        >
          {hasQuickControl && !isDeviceOn ? (
            <>
              <div className="group-hover:opacity-0 transition-opacity duration-150">
                <DeviceIcon deviceType={iconDeviceType as any} isOn={false} size={40} animated={true} />
              </div>
              <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                <DeviceIcon deviceType={iconDeviceType as any} isOn={true} size={40} animated={true} />
              </div>
            </>
          ) : (
            <DeviceIcon deviceType={iconDeviceType as any} isOn={isDeviceOn} size={40} animated={true} />
          )}
        </div>
        
        {/* TAP hint */}
        <div className="flex-[1] flex items-center justify-center">
          {hasQuickControl && (
            <span className={`text-[10px] font-bold transition-colors duration-150 ${
              isDeviceOn ? 'text-slate-800' : 'text-slate-400 group-hover:text-white group-hover:drop-shadow-[0_0_4px_rgba(255,255,255,0.5)]'
            }`}>
              {getTapHintText()}
            </span>
          )}
        </div>
      </div>

      {/* Right: Info Area */}
      <div className={`flex-1 p-3 flex flex-col min-w-0 ${getInfoAreaStyle()}`}>
        {/* Section 1: Name */}
        <div className="flex-[1] flex items-center">
          <h4 className="font-bold truncate text-sm text-white">{group.name}</h4>
        </div>
        
        {/* Section 2: State */}
        <div className="flex-[3] flex items-center gap-3">
          {renderState(state1, true)}
          {renderState(state2, false)}
        </div>
        
        {/* Section 3: Group indicator */}
        <div className="flex-[1] flex items-center justify-between">
          <div className="flex items-center gap-1">
            <LayersIcon className="w-3.5 h-3.5 text-blue-400" />
            <p className="text-xs font-bold text-slate-400">群組({group.devices.length})</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeviceGroupCard;
